import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import reducer, { initialState } from './state/reducers';
import actionCreators from './state/actionsCreators';

import FileList from './FileList';

const FileInput = ({
  accept,
  files,
  fileSelection,
  id,
  inputLabelProps,
  inputProps,
  inputWrapperProps,
  label,
  multiple,
  name,
  onAddFile,
  onDeleteFile,
  onFileSelected,
  selectedFileIndex,
  hideUploadButton
}) => {
  const [state, dispatch] = useReducer(
    reducer,
    initialState({ files, selectedFileIndex })
  );
  const actions = actionCreators(dispatch);

  const noFilesUploaded = !state.files.length;
  const showUploadButton = !hideUploadButton || noFilesUploaded;

  function handleFileChange(event) {
    const uploadedFiles = Array.from(event.target.files);
    /* Avoid break React event pooling with async call
    https://reactjs.org/docs/events.html#event-pooling
    */
    event.persist();

    actions.addFiles(
      uploadedFiles,
      newState => {
        onAddFile({ files: newState.files }, event);
      },
      multiple
    );
  }

  function handleDeleteFile(fileToBeRemoved) {
    return event => {
      /* Avoid break React event pooling with async call
      https://reactjs.org/docs/events.html#event-pooling
      */
      event.persist();
      actions.removeFile(fileToBeRemoved, newState => {
        onDeleteFile(
          {
            deletedFile: fileToBeRemoved,
            oldList: state.files,
            updatedList: newState.files
          },
          event
        );
      });
    };
  }

  function handleFileSelected(fileIndex) {
    return event => {
      /* Avoid break React event pooling with async call
      https://reactjs.org/docs/events.html#event-pooling
      */
      event.persist();
      actions.changeFile(fileIndex, newState => {
        onFileSelected(
          {
            fileSelected: newState.files[newState.selectedFileIndex],
            selectedFileIndex: newState.selectedFileIndex
          },
          event
        );
      });
    };
  }

  return (
    <>
      <div
        {...inputWrapperProps}
        className={classnames({ 'mb-1x pb-1x': !hideUploadButton })}
      >
        {showUploadButton && (
          <label
            {...inputLabelProps}
            className={classnames(
              'btn',
              'btn--wide',
              'bg--grey',
              'text--bold',
              'text--underline',
              'start-xs'
            )}
            htmlFor={name || id}
          >
            {label}
          </label>
        )}
        <input
          {...inputProps}
          style={{ display: 'none' }}
          id={name || id}
          type='file'
          accept={accept}
          onChange={handleFileChange}
          multiple={multiple}
        />
      </div>
      <FileList
        files={state.files}
        handleDeleteFile={handleDeleteFile}
        handleFileSelected={handleFileSelected}
        fileSelection={fileSelection}
        selectedFileIndex={state.selectedFileIndex}
        showUploadButton={showUploadButton}
      />
    </>
  );
};

FileInput.defaultProps = {
  fileSelection: false,
  label: 'Upload file',
  multiple: false
};

FileInput.propTypes = {
  /* Required props */
  name: PropTypes.string.isRequired,
  onAddFile: PropTypes.func.isRequired,
  onDeleteFile: PropTypes.func.isRequired,
  /* Not required props */
  accept: PropTypes.string,
  files: PropTypes.array,
  fileSelection: PropTypes.bool,
  hideUploadButton: PropTypes.bool,
  id: PropTypes.string,
  inputLabelProps: PropTypes.object,
  inputProps: PropTypes.object,
  inputWrapperProps: PropTypes.object,
  label: PropTypes.string,
  multiple: PropTypes.bool,
  selectedFileIndex: PropTypes.number,
  onFileSelected(props) {
    const { fileSelection, onFileSelected } = props;

    if (fileSelection && !onFileSelected) {
      return new TypeError(
        '"onFileSelected" is required when "fileSelection" is present'
      );
    }

    if (onFileSelected && typeof onFileSelected !== 'function') {
      return new TypeError('onFileSelected must be a function');
    }

    return null;
  }
};

export default FileInput;
